import React, { useState, useEffect } from 'react';
import { useErrand, CreateMultiStopErrandData, ErrandStop } from '../contexts/ErrandContext';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import AddressAutocomplete from './Map/AddressAutocomplete';
import LocationPicker from './Map/LocationPicker';
import { reverseGeocode } from '../utils/geocoding';
import PricingCalculator from './PricingCalculator';
import { API_ENDPOINTS } from '../config/api';

interface MultiStopErrandFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
}

export const MultiStopErrandForm: React.FC<MultiStopErrandFormProps> = ({
  onSuccess,
  onCancel
}) => {
  const { errandTypes, createMultiStopErrand, loading } = useErrand();
  const { user } = useAuth();

  const [formData, setFormData] = useState<CreateMultiStopErrandData>({
    errandTypeId: '',
    title: '',
    description: '',
    stops: [
      {
        sequence_number: 1,
        stop_type: 'pickup',
        location_name: '',
        latitude: undefined,
        longitude: undefined,
        address_details: '',
        contact_person: '',
        contact_phone: '',
        special_instructions: ''
      },
      {
        sequence_number: 2,
        stop_type: 'dropoff',
        location_name: '',
        latitude: undefined,
        longitude: undefined,
        address_details: '',
        contact_person: '',
        contact_phone: '',
        special_instructions: ''
      }
    ],
    preferredTimeSlot: 'now',
    scheduledTime: '',
    paymentMethod: 'wallet',
    specialInstructions: '',
    insuranceOpted: false
  });

  const [showMapForStop, setShowMapForStop] = useState<number | null>(null);
  const [userLocation, setUserLocation] = useState<{ latitude: number; longitude: number } | null>(null);
  const [demandData, setDemandData] = useState<{
    errands_pending_count: number;
    drivers_available_count: number;
    demand_ratio: number;
    pricing_multiplier: number;
    demand_level: string;
  } | null>(null);

  // Get user's current location on mount
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
        },
        (error) => {
          console.log('Geolocation error:', error);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000
        }
      );
    }
  }, []);

  // Fetch demand data on component mount
  useEffect(() => {
    const fetchDemandData = async () => {
      try {
        const response = await fetch(`${API_ENDPOINTS.BASE}/api/analytics/demand`);
        if (response.ok) {
          const data = await response.json();
          console.log('Demand data fetched:', data);
          setDemandData(data);
        } else {
          console.error('Demand API error:', response.status, response.statusText);
          // Set default values if fetch fails
          setDemandData({
            errands_pending_count: 0,
            drivers_available_count: 0,
            demand_ratio: 2.0,
            pricing_multiplier: 1.0,
            demand_level: 'normal'
          });
        }
      } catch (error) {
        console.error('Error fetching demand data:', error);
        // Set default values if fetch fails
        setDemandData({
          errands_pending_count: 0,
          drivers_available_count: 0,
          demand_ratio: 2.0,
          pricing_multiplier: 1.0,
          demand_level: 'normal'
        });
      }
    };
    fetchDemandData();
    // Refresh demand data every 30 seconds
    const interval = setInterval(fetchDemandData, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleStopLocationSelect = (stopIndex: number, address: string | null, lat: number, lng: number) => {
    const locationName = address || `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
    const updatedStops = [...formData.stops];
    updatedStops[stopIndex] = {
      ...updatedStops[stopIndex],
      location_name: locationName,
      latitude: lat,
      longitude: lng
    };
    setFormData({ ...formData, stops: updatedStops });
    setShowMapForStop(null);
  };

  const handleStopChange = (index: number, field: keyof ErrandStop, value: any) => {
    const updatedStops = [...formData.stops];
    updatedStops[index] = { ...updatedStops[index], [field]: value };
    setFormData({ ...formData, stops: updatedStops });
  };

  const addStop = () => {
    const newStop: Omit<ErrandStop, 'id' | 'errand_id' | 'status' | 'created_at' | 'updated_at'> = {
      sequence_number: formData.stops.length + 1,
      stop_type: 'intermediate',
      location_name: '',
      latitude: undefined,
      longitude: undefined,
      address_details: '',
      contact_person: '',
      contact_phone: '',
      special_instructions: ''
    };
    setFormData({
      ...formData,
      stops: [...formData.stops, newStop]
    });
  };

  const removeStop = (index: number) => {
    if (formData.stops.length <= 2) {
      toast.error('Multi-stop errands must have at least 2 stops');
      return;
    }

    const updatedStops = formData.stops.filter((_, i) => i !== index);
    // Re-sequence the stops
    updatedStops.forEach((stop, i) => {
      stop.sequence_number = i + 1;
    });

    setFormData({ ...formData, stops: updatedStops });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.errandTypeId || !formData.title) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (formData.stops.length < 2) {
      toast.error('Multi-stop errands must have at least 2 stops');
      return;
    }

    // Validate stops
    for (let i = 0; i < formData.stops.length; i++) {
      const stop = formData.stops[i];
      if (!stop.location_name || !stop.stop_type) {
        toast.error(`Stop ${i + 1} is missing required information`);
        return;
      }
      // Validate coordinates
      if (stop.latitude === undefined || stop.latitude === null ||
          stop.longitude === undefined || stop.longitude === null) {
        toast.error(`Stop ${i + 1} (${stop.location_name}) is missing coordinates. Please select a location from the suggestions or map.`);
        return;
      }
      // Validate coordinate ranges
      if (stop.latitude < -90 || stop.latitude > 90 || stop.longitude < -180 || stop.longitude > 180) {
        toast.error(`Stop ${i + 1} has invalid coordinates. Please reselect the location.`);
        return;
      }
    }

    // Ensure we have at least one pickup and one dropoff
    const pickups = formData.stops.filter(stop => stop.stop_type === 'pickup');
    const dropoffs = formData.stops.filter(stop => stop.stop_type === 'dropoff');

    if (pickups.length === 0) {
      toast.error('Multi-stop errands must have at least one pickup stop');
      return;
    }

    if (dropoffs.length === 0) {
      toast.error('Multi-stop errands must have at least one dropoff stop');
      return;
    }

    try {
      const result = await createMultiStopErrand(formData);
      if (result) {
        toast.success('Multi-stop errand created successfully!');
        onSuccess?.();
      } else {
        toast.error('Failed to create multi-stop errand');
      }
    } catch (error: any) {
      console.error('Multi-stop errand creation error:', error);
      toast.error(error.message || 'An error occurred while creating the errand');
    }
  };

  return (
    <div className="max-w-4xl mx-auto bg-white rounded-lg shadow p-6">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Create Multi-Stop Errand</h2>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Errand Type *
            </label>
            <select
              value={formData.errandTypeId}
              onChange={(e) => setFormData({ ...formData, errandTypeId: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="">Select errand type</option>
              {errandTypes.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Title *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Brief description of the errand"
              required
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Description
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Detailed description of what needs to be done"
          />
        </div>

        {/* Stops */}
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Stops</h3>
            <button
              type="button"
              onClick={addStop}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              Add Stop
            </button>
          </div>

          <div className="space-y-4">
            {formData.stops.map((stop, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-medium text-gray-900">Stop {index + 1}</h4>
                  {formData.stops.length > 2 && (
                    <button
                      type="button"
                      onClick={() => removeStop(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      Remove
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Stop Type *
                    </label>
                    <select
                      value={stop.stop_type}
                      onChange={(e) => handleStopChange(index, 'stop_type', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="pickup">Pickup</option>
                      <option value="dropoff">Dropoff</option>
                      <option value="intermediate">Intermediate</option>
                      <option value="return">Return</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Location Name *
                    </label>
                    <div className="flex space-x-2">
                      <input
                        type="text"
                        value={stop.location_name}
                        onChange={(e) => handleStopChange(index, 'location_name', e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Location name or address"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowMapForStop(index)}
                        className="px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                        title="Select location on map"
                      >
                        📍
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Contact Person
                    </label>
                    <input
                      type="text"
                      value={stop.contact_person || ''}
                      onChange={(e) => handleStopChange(index, 'contact_person', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Person to contact at this stop"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Contact Phone
                    </label>
                    <input
                      type="tel"
                      value={stop.contact_phone || ''}
                      onChange={(e) => handleStopChange(index, 'contact_phone', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Phone number"
                    />
                  </div>
                </div>

                <div className="mt-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Special Instructions
                  </label>
                  <textarea
                    value={stop.special_instructions || ''}
                    onChange={(e) => handleStopChange(index, 'special_instructions', e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Any special instructions for this stop"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Scheduling and Payment */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              When do you need this done?
            </label>
            <select
              value={formData.preferredTimeSlot}
              onChange={(e) => setFormData({ ...formData, preferredTimeSlot: e.target.value as 'now' | 'schedule' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="now">As soon as possible</option>
              <option value="schedule">Schedule for later</option>
            </select>
          </div>

          {formData.preferredTimeSlot === 'schedule' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Scheduled Time
              </label>
              <input
                type="datetime-local"
                value={formData.scheduledTime}
                onChange={(e) => setFormData({ ...formData, scheduledTime: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Payment Method
            </label>
            <select
              value={formData.paymentMethod}
              onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value as 'wallet' | 'cash_on_delivery' | 'admin_escrow' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="wallet">Wallet</option>
              <option value="cash_on_delivery">Cash on Delivery</option>
              <option value="admin_escrow">Admin Escrow</option>
            </select>
          </div>
        </div>

        {/* Special Instructions */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Special Instructions
          </label>
          <textarea
            value={formData.specialInstructions}
            onChange={(e) => setFormData({ ...formData, specialInstructions: e.target.value })}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Any additional instructions for the driver"
          />
        </div>

        {/* Pricing Calculator */}
        <PricingCalculator
          errandType={errandTypes.find(type => type.id === formData.errandTypeId) || null}
          demandData={demandData}
          stops={formData.stops}
        />

        {/* Insurance Option */}
        <div className="flex items-center">
          <input
            type="checkbox"
            id="insurance"
            checked={formData.insuranceOpted}
            onChange={(e) => setFormData({ ...formData, insuranceOpted: e.target.checked })}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <label htmlFor="insurance" className="ml-2 text-sm text-gray-700">
            Add insurance coverage for this errand
          </label>
        </div>

        {/* Submit Buttons */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Creating...' : 'Create Multi-Stop Errand'}
          </button>
        </div>
      </form>

      {/* Map Modal for Stops */}
      {showMapForStop !== null && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-hidden">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900">Select Location for Stop {showMapForStop + 1}</h3>
              <button
                onClick={() => setShowMapForStop(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4 space-y-4">
              <AddressAutocomplete
                value={formData.stops[showMapForStop]?.location_name ?? ''}
                onChange={(address, lat, lng) => {
                  handleStopLocationSelect(showMapForStop, address, lat, lng);
                }}
                placeholder={`Search for Stop ${showMapForStop + 1} location...`}
              />
              <LocationPicker
                latitude={userLocation?.latitude ?? -24.6282}
                longitude={userLocation?.longitude ?? 25.9231}
                onLocationChange={(lat, lng) => {
                  // Reverse geocode to get address
                  if (navigator.geolocation) {
                    reverseGeocode(lat, lng).then(address => {
                      handleStopLocationSelect(showMapForStop, address, lat, lng);
                    }).catch(() => {
                      // Fallback to coordinates as address
                      handleStopLocationSelect(showMapForStop, `${lat.toFixed(6)}, ${lng.toFixed(6)}`, lat, lng);
                    });
                  } else {
                    handleStopLocationSelect(showMapForStop, `${lat.toFixed(6)}, ${lng.toFixed(6)}`, lat, lng);
                  }
                }}
                height="300px"
                zoom={13}
              />
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowMapForStop(null)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => setShowMapForStop(null)}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  Confirm Location
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
